import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type Modifier,
} from '@dnd-kit/core';

import { Button } from '../../../shared/ui/button';
import { Checkbox } from '../../../shared/ui/checkbox';
import { MoveToFolderIcon } from '../../../shared/ui/icons';
import { MenuItem } from '../../../shared/ui/menu';
import { MutationError } from '../../../shared/ui/mutation-error';
import { InertCell, RowMenu } from '../../../shared/ui/row-menu';
import { EmptyState } from '../../../shared/ui/states';
import { Table, type IColumn } from '../../../shared/ui/table';
import {
  SCOPE_ROOT_LABEL,
  childFolders,
  folderKeys,
  listFolders,
  moveFolder,
  moveItems,
  subtreeIds,
  type TFolder,
  type TFolderCrumb,
  type TFolderScope,
} from '../../../entities/folder';
import { FolderDelete, FolderRename } from '../../../features/folder-actions';
import { MoveToFolder } from '../../../features/folder-move';
import type { IDropTarget, TDragged } from '../lib';
import { DraggableRow } from './draggable-row';
import { FolderCrumbs } from './folder-crumbs';
import { FolderRow } from './folder-row';

// The overlay starts at the row's corner; the chip should ride next to the pointer instead.
const besideCursor: Modifier = ({
  activatorEvent,
  draggingNodeRect,
  overlayNodeRect,
  transform,
}) => {
  if (!(activatorEvent instanceof PointerEvent) || !draggingNodeRect) return transform;
  return {
    ...transform,
    x: transform.x + activatorEvent.clientX - draggingNodeRect.left + 12,
    y:
      transform.y +
      activatorEvent.clientY -
      draggingNodeRect.top -
      (overlayNodeRect?.height ?? 0) / 2,
  };
};

interface IFolderBrowserProps<TRow> {
  scope: TFolderScope;
  /** One item, for counts and sentences: "flow", "item". */
  noun: string;
  folderId: string | null;
  onFolder: (id: string | null) => void;
  /** A search runs across every folder: no folder rows; the page's name cell shows the path. */
  searching: boolean;
  rows: readonly TRow[];
  /** The items' query has answered; until then no empty state is claimed. */
  ready: boolean;
  columns: readonly IColumn<TRow>[];
  rowKey: (row: TRow) => string;
  rowLabel: (row: TRow) => string;
  rowPath: (row: TRow) => readonly TFolderCrumb[];
  onRowClick?: (row: TRow) => void;
  tall?: boolean;
  layoutId?: string;
  /** In place of the table when nothing is listed; absent: "This folder is empty" in a folder. */
  empty?: ReactNode;
  /** The page's own item states (loading, error), under the breadcrumb. */
  children?: ReactNode;
  footer?: ReactNode;
}

export const FolderBrowser = <TRow,>({
  scope,
  noun,
  folderId,
  onFolder,
  searching,
  rows,
  ready,
  columns,
  rowKey,
  rowLabel,
  rowPath,
  onRowClick,
  tall,
  layoutId,
  empty,
  children,
  footer,
}: IFolderBrowserProps<TRow>) => {
  const client = useQueryClient();
  const folders = useQuery({ queryKey: folderKeys.list(scope), queryFn: () => listFolders(scope) });
  const all = useMemo(() => folders.data?.data ?? [], [folders.data]);
  const current = folderId ? (all.find((folder) => folder.id === folderId) ?? null) : null;
  const missing = Boolean(folderId) && folders.isSuccess && !current;
  const subfolders = searching || missing ? [] : childFolders(all, folderId);

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [moving, setMoving] = useState<string[] | null>(null);
  const [dialog, setDialog] = useState<{
    kind: 'rename' | 'move' | 'delete';
    folder: TFolder;
  } | null>(null);
  const [dragged, setDragged] = useState<TDragged | null>(null);
  const [dropError, setDropError] = useState<unknown>(null);

  useEffect(() => {
    setSelected(new Set());
  }, [folderId, searching]);

  const keys = useMemo(() => new Set(rows.map(rowKey)), [rows, rowKey]);
  const chosen = [...selected].filter((key) => keys.has(key));
  const byKey = useMemo(() => new Map(rows.map((row) => [rowKey(row), row])), [rows, rowKey]);
  const labelOf = (key: string) => {
    const row = byKey.get(key);
    return row ? rowLabel(row) : noun;
  };

  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: folderKeys.list(scope) }),
      client.invalidateQueries({ queryKey: [scope === 'flows' ? 'flows' : 'library'] }),
    ]);

  const moveRows = async (ids: readonly string[], target: string | null) => {
    await moveItems(scope, ids, target);
    setSelected(new Set());
    await refresh();
  };

  const moveOneFolder = async (folder: TFolder, target: string | null) => {
    await moveFolder(folder.id, target);
    await refresh();
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const onDragStart = (event: DragStartEvent) => {
    setDropError(null);
    setDragged((event.active.data.current as TDragged | undefined) ?? null);
  };

  const onDragEnd = (event: DragEndEvent) => {
    setDragged(null);
    const what = event.active.data.current as TDragged | undefined;
    const target = event.over?.data.current as IDropTarget | undefined;
    if (!what || !target) return;
    const run = async () => {
      if (what.kind === 'folder') {
        if (target.folderId === what.folder.parentId) return;
        if (target.folderId && subtreeIds(all, what.folder.id).has(target.folderId)) return;
        await moveOneFolder(what.folder, target.folderId);
        return;
      }
      // Dragging one of the ticked rows takes all of them, as in a file manager.
      const ids = selected.has(what.id) ? chosen : [what.id];
      const unmoved = ids.filter((id) => {
        const row = byKey.get(id);
        const at = row ? rowPath(row) : [];
        return (at[at.length - 1]?.id ?? null) !== target.folderId;
      });
      if (unmoved.length > 0) await moveRows(unmoved, target.folderId);
    };
    run().catch(setDropError);
  };

  const toggle = (key: string, on: boolean) =>
    setSelected((was) => {
      const next = new Set(was);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  const shownColumns: IColumn<TRow>[] = [
    {
      key: 'select',
      header: '',
      width: 'w-col-select',
      ui: true,
      render: (row) => (
        <InertCell>
          <Checkbox
            aria-label={`Select ${rowLabel(row)}`}
            checked={selected.has(rowKey(row))}
            onCheckedChange={(on) => toggle(rowKey(row), on)}
          />
        </InertCell>
      ),
    },
    ...columns,
    {
      key: 'menu',
      header: '',
      width: 'w-col-row-menu',
      ui: true,
      render: (row) => (
        <RowMenu label={rowLabel(row)}>
          <MenuItem onSelect={() => setMoving([rowKey(row)])}>
            <MoveToFolderIcon />
            Move to…
          </MenuItem>
        </RowMenu>
      ),
    },
  ];
  // At the top level the page's own title already names the list; crumbs only once inside.
  const showCrumbs = Boolean(folderId) && !searching;

  const nothing = ready && folders.isSuccess && subfolders.length === 0 && rows.length === 0;
  const movingOne = moving?.length === 1 ? byKey.get(moving[0]) : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setDragged(null)}
    >
      <div className="flex flex-col gap-section-gap">
        {(showCrumbs || searching || chosen.length > 0) && (
          <div className="flex min-h-control-sm flex-wrap items-center gap-inset">
            {showCrumbs && (
              <FolderCrumbs scope={scope} path={current?.path ?? []} onFolder={onFolder} />
            )}
            {searching && (
              <span className="font-mono text-micro text-muted">searching every folder</span>
            )}
            {chosen.length > 0 && (
              <span className="ml-auto flex items-center gap-2">
                <span className="text-data text-muted">
                  {chosen.length.toLocaleString('en-US')} selected
                </span>
                <Button size="sm" variant="secondary" onClick={() => setMoving(chosen)}>
                  <MoveToFolderIcon />
                  Move to…
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
              </span>
            )}
          </div>
        )}

        <MutationError error={dropError ?? folders.error} />

        {children}

        {missing && (
          <EmptyState
            title="This folder is not here any more"
            description="It was deleted, or the link is from another workspace. Deleting a folder never deletes what was in it."
            action={
              <Button size="sm" variant="secondary" onClick={() => onFolder(null)}>
                Go to {SCOPE_ROOT_LABEL[scope]}
              </Button>
            }
          />
        )}

        {!missing && (subfolders.length > 0 || rows.length > 0) && (
          <Table
            tall={tall}
            layoutId={layoutId}
            columns={shownColumns}
            rows={rows}
            rowKey={rowKey}
            onRowClick={onRowClick}
            wrapRow={(row, drawn) => <DraggableRow id={rowKey(row)}>{drawn}</DraggableRow>}
            lead={subfolders.map((folder) => (
              <FolderRow
                key={folder.id}
                folder={folder}
                noun={noun}
                onOpen={(next) => onFolder(next.id)}
                onRename={(next) => setDialog({ kind: 'rename', folder: next })}
                onMove={(next) => setDialog({ kind: 'move', folder: next })}
                onDelete={(next) => setDialog({ kind: 'delete', folder: next })}
              />
            ))}
          />
        )}

        {!missing &&
          nothing &&
          (empty ??
            (current ? (
              <EmptyState
                title="This folder is empty"
                description={`Nothing is filed here yet. Drag a ${noun} onto this folder one level up, or use “Move to…” on any ${noun}.`}
              />
            ) : null))}

        {footer}
      </div>

      {/* On body: a transformed ancestor turns the overlay's `fixed` into an offset box. */}
      {createPortal(
        <DragOverlay dropAnimation={null} modifiers={[besideCursor]}>
          {dragged && (
            <span className="inline-flex items-center gap-2 rounded-control border border-border-raised bg-surface-raised px-flat py-label text-data text-primary shadow-panel">
              <MoveToFolderIcon />
              {dragged.kind === 'folder'
                ? dragged.folder.name
                : selected.has(dragged.id) && chosen.length > 1
                  ? `${chosen.length.toLocaleString('en-US')} ${noun}s`
                  : labelOf(dragged.id)}
            </span>
          )}
        </DragOverlay>,
        document.body,
      )}

      <MoveToFolder
        scope={scope}
        noun={noun}
        open={moving !== null}
        onOpenChange={(open) => !open && setMoving(null)}
        title={
          movingOne
            ? `Move “${rowLabel(movingOne)}”`
            : `Move ${(moving?.length ?? 0).toLocaleString('en-US')} ${noun}s`
        }
        from={searching ? undefined : folderId}
        onMove={(target) => moveRows(moving ?? [], target)}
      />

      {dialog?.kind === 'move' && (
        <MoveToFolder
          scope={scope}
          noun={noun}
          open
          onOpenChange={(open) => !open && setDialog(null)}
          title={`Move “${dialog.folder.name}”`}
          from={dialog.folder.parentId}
          folder={dialog.folder}
          onMove={(target) => moveOneFolder(dialog.folder, target)}
        />
      )}
      {dialog?.kind === 'rename' && (
        <FolderRename
          folder={dialog.folder}
          open
          onOpenChange={(open) => !open && setDialog(null)}
        />
      )}
      {dialog?.kind === 'delete' && (
        <FolderDelete
          folder={dialog.folder}
          noun={noun}
          open
          onOpenChange={(open) => !open && setDialog(null)}
        />
      )}
    </DndContext>
  );
};
